"use client"

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../../contexts/auth-context'
import { useNavigate } from 'react-router-dom'
import { LogOut, CreditCard, ChevronLeft, AlertTriangle, ExternalLink, Sparkles } from 'lucide-react'
import { useSubscription } from '../../contexts/subscription-context'
import { SubscriptionService } from '../../services/subscription-service'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../../lib/firebase'
import UsageStats from './UsageStats'

export default function Settings() {
  const { user, logout, updateEmail } = useAuth()
  const { subscription } = useSubscription()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteEmail, setDeleteEmail] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [isEditingEmail, setIsEditingEmail] = useState(false)

  // Helper functions to check subscription status
  const isProPlan = () => subscription?.status === 'active' && subscription?.tier === 'pro';
  const isUltimatePlan = () => subscription?.status === 'active' && subscription?.tier === 'ultimate';
  const isActivePlan = () => subscription?.status === 'active';
  
  // Get plan name based on subscription tier
  const getPlanName = () => {
    if (!isActivePlan()) return 'Free Plan';
    if (isUltimatePlan()) return 'Ultimate Plan';
    return 'Pro Plan';
  };

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      setMessage({ type: 'error', text: error.message })
    }
  }

  const handleManageSubscription = async () => {
    // For free users, we want to redirect to plans page to subscribe
    if (!subscription || !subscription.status) {
      navigate('/plans')
      return
    }

    // For cancelled subscriptions, we want to redirect to plans page to resubscribe
    if (!subscription?.customerId || subscription?.status === 'canceled') {
      navigate('/plans')
      return
    }
    try {
      setLoading(true)
      await SubscriptionService.manageSubscription()
    } catch (error) {
      setMessage({ type: 'error', text: error.message })
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (deleteEmail !== user?.email) {
      setMessage({ type: 'error', text: 'Please enter your email correctly to confirm deletion' })
      return
    }

    if (subscription?.status === 'active') {
      setMessage({ type: 'error', text: 'Please cancel your subscription before deleting your account' })
      return
    }

    try {
      setLoading(true)
      const deleteAccount = httpsCallable(functions, 'deleteUserAccount')
      await deleteAccount()
      await logout()
      navigate('/')
      window.scrollTo(0, 0)
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to delete account' })
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateEmail = async (e) => {
    e.preventDefault()
    if (!newEmail) {
      setMessage({ type: 'error', text: 'Please enter a new email address' })
      return
    }
    
    try {
      setLoading(true)
      await updateEmail(newEmail)
      setMessage({ type: 'success', text: 'Verification email sent to your new address. Please check your inbox and verify the new email.' })
      setIsEditingEmail(false)
      setNewEmail('')
    } catch (error) {
      setMessage({ type: 'error', text: error.message })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <header className="fixed top-0 w-full bg-black/50 backdrop-blur-md border-b border-gray-800 z-10">
        <nav className="container mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => navigate('/chat')}
            className="text-gray-400 hover:text-gray-300 flex items-center"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back
          </button>
        </nav>
      </header>

      <div className="container mx-auto px-4 py-8 pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          <h1 className="text-3xl font-bold mb-8 bg-gradient-to-r from-purple-400 to-pink-600 text-transparent bg-clip-text">
            Settings
          </h1>

          {message && (
            <div className={`mb-6 p-4 rounded-lg ${
              message.type === 'error' ? 'bg-red-500/10 border border-red-500/20 text-red-400' : 
              'bg-green-500/10 border border-green-500/20 text-green-400'
            }`}>
              {message.text}
            </div>
          )}

          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-800 p-8 mb-6">
            <h2 className="text-xl font-semibold text-white mb-6">Profile</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-1">Email</label>
                {!isEditingEmail ? (
                  <div className="flex items-center space-x-4">
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="flex-1 px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700 text-white"
                    />
                    <button
                      onClick={() => setIsEditingEmail(true)}
                      className="px-4 py-2 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-lg hover:bg-purple-500/20 transition-colors"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleUpdateEmail} className="space-y-4">
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Enter new email"
                      className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700 text-white placeholder-gray-500"
                    />
                    <div className="flex space-x-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50"
                      >
                        {loading ? 'Sending...' : 'Update Email'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditingEmail(false)
                          setNewEmail('')
                        }}
                        className="px-4 py-2 text-gray-400 hover:text-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-800 p-8 mb-6">
            <h2 className="text-xl font-semibold text-white mb-6">Subscription</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-300">Current Plan</p>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-semibold text-white">
                      {getPlanName()}
                    </p>
                    {isUltimatePlan() && (
                      <span className="text-sm font-medium bg-gradient-to-r from-blue-400 via-violet-400 to-indigo-400 text-transparent bg-clip-text border border-indigo-400/50 rounded px-1.5 flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-indigo-400" />
                        Ultimate
                      </span>
                    )}
                    {isProPlan() && (
                      <span className="text-sm font-medium text-purple-400 border border-purple-400/50 rounded px-1.5">
                        Pro
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col md:flex-row gap-2">
                  <button
                    onClick={() => navigate('/plans')}
                    className="flex items-center justify-center px-4 py-2 text-gray-300 border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors text-sm"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    View Plans
                  </button>
                  <button
                    onClick={handleManageSubscription}
                    disabled={loading}
                    className="flex items-center justify-center px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 text-sm"
                  >
                    {isActivePlan() ? (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        {loading ? 'Loading...' : 'Manage Subscription'}
                      </>
                    ) : (
                      <>Upgrade</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <UsageStats />

          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-red-900/20 p-8 mb-6">
            <h2 className="text-xl font-semibold text-red-400 mb-6 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Danger Zone
            </h2>
            
            <div className="space-y-4">
              <p className="text-gray-400">
                Deleting your account will permanently remove all your data, including all your debates. This action cannot be undone.
              </p>
              
              {isActivePlan() && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-red-400">
                    Please cancel your subscription before deleting your account.
                  </p>
                </div>
              )}

              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors"
                >
                  Delete Account
                </button>
              ) : (
                <div className="space-y-4">
                  <p className="text-gray-300">
                    Please type your email <span className="font-semibold">{user?.email}</span> to confirm:
                  </p>
                  <input
                    type="email"
                    value={deleteEmail}
                    onChange={(e) => setDeleteEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700 text-white placeholder-gray-500"
                  />
                  <div className="flex space-x-4">
                    <button
                      onClick={handleDeleteAccount}
                      disabled={loading || isActivePlan() || deleteEmail !== user?.email}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Deleting...' : 'Confirm Delete'}
                    </button>
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false)
                        setDeleteEmail('')
                      }}
                      className="px-4 py-2 text-gray-400 hover:text-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {message && (
            <div className={`mb-4 p-3 rounded ${
              message.type === 'error' ? 'bg-red-500/10 border-red-500/50 text-red-500' : 'bg-green-500/10 border-green-500/50 text-green-500'
            } border`}>
              {message.text}
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <button
              onClick={handleLogout}
              className="flex items-center px-4 py-2 text-red-400 hover:text-red-300 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  )
}